// ===================================
// KAIXO - Onboarding (solo primera vez)
// 3 slides + selector tipo de cuenta
// ===================================

import React, { useState } from 'react';
import './Onboarding.css';
import { Icons } from '../Icons/Icons';
import { getInterestLabel } from '../../constants/interests';
import InterestPickerModal from '../InterestPicker/InterestPickerModal';

const SLIDES = {
    es: {
        personal: [
            { icon: 'globe', title: 'Habla en cualquier idioma', desc: 'Envía mensajes en tu idioma y la otra persona los recibe traducidos automáticamente. 15 idiomas disponibles.' },
            { icon: 'group', title: 'Grupos sin fronteras', desc: 'Crea grupos donde cada persona lee en su idioma. 5 idiomas, 5 traducciones automáticas simultáneas.' },
            { icon: 'share', title: 'Comparte todo', desc: 'Fotos, audios transcritos y traducidos, videos con subtítulos, documentos. Todo se adapta al idioma del receptor.' }
        ],
        business: [
            { icon: 'pulse', title: 'Publica y llega al mundo', desc: 'Crea tu Latido y publica contenido que llega traducido automáticamente a cada seguidor en su idioma.' },
            { icon: 'users', title: 'Tus clientes vienen a ti', desc: 'Comparte tu enlace, recibe consultas con formulario y cada contacto se guarda automáticamente en tu CRM.' },
            { icon: 'store', title: 'Gestiona tu equipo', desc: 'Asigna contactos a tus agentes, mide rendimiento con puntos y reportes, y cierra más ventas.' }
        ]
    },
    en: {
        personal: [
            { icon: 'globe', title: 'Speak any language', desc: 'Send messages in your language and the other person receives them automatically translated. 15 languages.' },
            { icon: 'group', title: 'Groups without borders', desc: 'Create groups where each person reads in their language. 5 languages, 5 simultaneous translations.' },
            { icon: 'share', title: 'Share everything', desc: 'Photos, transcribed audio, videos with subtitles, documents. Everything adapts to the recipient language.' }
        ],
        business: [
            { icon: 'pulse', title: 'Publish and reach the world', desc: 'Create your Heartbeat and publish content that reaches every follower automatically translated.' },
            { icon: 'users', title: 'Clients come to you', desc: 'Share your link, receive inquiries with forms, every contact saves automatically to your CRM.' },
            { icon: 'store', title: 'Manage your team', desc: 'Assign contacts to agents, measure performance with points and reports, close more deals.' }
        ]
    }
};

function AnimatedLogo() {
    return (
        <div className="onb-logo-drop">
            <img src={require('../../assets/favicon.png')} alt="KAIXO" className="onb-logo-img" />
        </div>
    );
}

function Onboarding({ onComplete }) {
    const [chosenType, setChosenType] = useState(null); // null=elegir, 'personal'|'business'
    const [slideIdx, setSlideIdx] = useState(0);
    const [showInterests, setShowInterests] = useState(false);
    const [selectedInterests, setSelectedInterests] = useState([]);
    const [showInterestPicker, setShowInterestPicker] = useState(false);

    const lang = (navigator.language || 'es').startsWith('es') ? 'es' : 'en';
    const localeForLabels = lang === 'es' ? 'español' : 'inglés';
    const t = lang === 'es' ? {
        question: '¿Cómo vas a usar KAIXO?',
        subtitle: 'Puedes cambiarlo después en Ajustes.',
        personal: 'Personal', personalDesc: 'Chatear con amigos y familia en cualquier idioma',
        business: 'Negocio', businessDesc: 'Publicar, vender y gestionar contactos multilingües',
        next: 'Siguiente', back: 'Atrás', start: '¡Empezar!', skip: 'Saltar',
        interestsTitle: '¿Qué te interesa?',
        interestsDesc: 'Selecciona temas para recibir Latidos relevantes. Puedes cambiar esto después.',
        interestsHint: 'Mínimo 0, máximo 10 seleccionados.',
        addInterest: 'Añadir interés'
    } : {
        question: 'How will you use KAIXO?',
        subtitle: 'You can change this anytime in Settings.',
        personal: 'Personal', personalDesc: 'Chat with friends and family in any language',
        business: 'Business', businessDesc: 'Publish, sell and manage multilingual contacts',
        next: 'Next', back: 'Back', start: "Let's go!", skip: 'Skip',
        interestsTitle: 'What are you into?',
        interestsDesc: 'Pick topics to receive relevant Heartbeats. You can change this later.',
        interestsHint: 'Minimum 0, maximum 10 selected.',
        addInterest: 'Add interest'
    };

    const removeInterest = (key) => {
        setSelectedInterests(prev => prev.filter(i => i !== key));
    };

    const iconMap = {
        globe: <Icons.Globe size={48} />,
        pulse: <Icons.Pulse size={48} />,
        users: <Icons.Users size={48} />,
        group: <Icons.Group size={48} />,
        share: <Icons.Share size={48} />,
        store: <Icons.Store size={48} />
    };

    const handleFinish = () => {
        try { localStorage.setItem('kaixo_onboarding_done', '1'); } catch {}
        onComplete(chosenType, selectedInterests);
    };

    // Paso 3: intereses
    if (showInterests) {
        const isBusiness = chosenType === 'business';
        return (
            <div className="onb-overlay">
                <div className="onb-container">
                    <div className="onb-slide">
                        <div className={`onb-icon ${isBusiness ? 'business-bg' : ''}`}>
                            <Icons.Pulse size={48} />
                        </div>
                        <h2 className="onb-title">{t.interestsTitle}</h2>
                        <p className="onb-desc">{t.interestsDesc}</p>
                        <p className="onb-subdesc">{t.interestsHint}</p>
                    </div>
                    <button
                        type="button"
                        className="onb-interest-select"
                        onClick={() => setShowInterestPicker(true)}
                        style={{ textAlign: 'left', cursor: 'pointer' }}
                    >
                        + {t.addInterest || 'Seleccionar intereses'}
                    </button>
                    {selectedInterests.length > 0 && (
                        <div className="onb-interests-grid onb-selected">
                            {selectedInterests.map(key => (
                                <button
                                    key={key}
                                    className={`onb-interest-chip active ${isBusiness ? 'business' : ''}`}
                                    onClick={() => removeInterest(key)}
                                    type="button"
                                >
                                    {getInterestLabel(key, localeForLabels)} ×
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="onb-actions">
                        <button className="onb-btn-secondary" onClick={() => setShowInterests(false)}>
                            <Icons.ArrowBack size={16} /> {t.back}
                        </button>
                        <button className={`onb-btn-primary ${isBusiness ? 'business-btn' : ''}`} onClick={handleFinish}>
                            {t.start}
                        </button>
                    </div>
                    <button className="onb-skip" onClick={handleFinish}>
                        {t.skip}
                    </button>
                </div>
                {showInterestPicker && (
                    <InterestPickerModal
                        value={selectedInterests}
                        max={10}
                        language={localeForLabels === 'inglés' ? 'inglés' : 'español'}
                        accent={isBusiness ? '#00b4d8' : '#ff6b35'}
                        onSave={(next) => setSelectedInterests(next)}
                        onClose={() => setShowInterestPicker(false)}
                    />
                )}
            </div>
        );
    }

    // Paso 1: elegir tipo de cuenta
    if (!chosenType) {
        return (
            <div className="onb-overlay">
                <div className="onb-container">
                    <AnimatedLogo />
                    <div className="onb-slide">
                        <h2 className="onb-title" style={{ marginTop: 16 }}>KAIXO</h2>
                        <p className="onb-desc" style={{ marginBottom: 10 }}>{t.question}</p>
                        <p className="onb-subdesc">{t.subtitle}</p>
                    </div>
                    <div className="onb-type-cards">
                        <button className="onb-type-card" onClick={() => { setChosenType('personal'); setSlideIdx(0); }}>
                            <div className="onb-type-icon personal"><Icons.User size={32} /></div>
                            <div className="onb-type-title">{t.personal}</div>
                            <div className="onb-type-desc">{t.personalDesc}</div>
                        </button>
                        <button className="onb-type-card" onClick={() => { setChosenType('business'); setSlideIdx(0); }}>
                            <div className="onb-type-icon business"><Icons.Store size={32} /></div>
                            <div className="onb-type-title">{t.business}</div>
                            <div className="onb-type-desc">{t.businessDesc}</div>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 2: slides adaptadas al tipo elegido
    const allSlides = SLIDES[lang] || SLIDES.en;
    const slides = allSlides[chosenType] || allSlides.personal;
    const isBusiness = chosenType === 'business';
    const s = slides[slideIdx];
    const isLast = slideIdx === slides.length - 1;

    return (
        <div className="onb-overlay">
            <div className="onb-container">
                <div className="onb-slide" key={slideIdx}>
                    <div className={`onb-icon ${isBusiness ? 'business-bg' : ''}`}>
                        {iconMap[s.icon] || <Icons.Globe size={48} />}
                    </div>
                    <h2 className="onb-title">{s.title}</h2>
                    <p className="onb-desc">{s.desc}</p>
                </div>

                <div className="onb-dots">
                    {slides.map((_, i) => (
                        <div key={i} className={`onb-dot ${i === slideIdx ? 'active' : ''}`}
                            style={isBusiness && i === slideIdx ? { background: '#00b4d8' } : {}} />
                    ))}
                </div>

                <div className="onb-actions">
                    <button className="onb-btn-secondary" onClick={() => {
                        if (slideIdx > 0) setSlideIdx(slideIdx - 1);
                        else setChosenType(null);
                    }}>
                        <Icons.ArrowBack size={16} /> {t.back}
                    </button>
                    {isLast ? (
                        <button className={`onb-btn-primary ${isBusiness ? 'business-btn' : ''}`} onClick={() => setShowInterests(true)}>
                            {t.next} <Icons.ChevronRight size={16} />
                        </button>
                    ) : (
                        <button className={`onb-btn-primary ${isBusiness ? 'business-btn' : ''}`} onClick={() => setSlideIdx(slideIdx + 1)}>
                            {t.next} <Icons.ChevronRight size={16} />
                        </button>
                    )}
                </div>

                <button className="onb-skip" onClick={handleFinish}>
                    {t.skip}
                </button>
            </div>
        </div>
    );
}

export default Onboarding;
